import ExcelJS from 'exceljs'
import dayjs from 'dayjs'

import { downloadXlsx, XLSX_HEADER_FILL, xlsxFreezePane } from 'helpers/xlsx'

import { BenefitRow } from '../../../api/reconciliation-v2-api-client'

import {
  buildPeriodKey,
  formatPaymentLabel,
  MONTHS,
  monthCoverage,
  primaryInsured,
} from './helpers'

/**
 * Benefits tab XLSX export — reproduces the finance "Enrollments Master
 * (Payments)" spreadsheet from recon-v2 data: one row per benefit, an identity
 * block, then a four-column block (Prem / Draft / Trans / Date) per visible
 * month, closed by Sum Prem / Sum Draft / DIFF / Zendesk.
 *
 * Only columns recon-v2 can populate are emitted (Broker, AP, VCN TokenID, etc.
 * from the source sheet are intentionally dropped). Months mirror the on-screen
 * visible range; the sums span exactly those months so they reconcile against
 * the grid the operator exported from.
 */

/** Placeholder the source sheet uses for months outside a benefit's window. */
const OUT_OF_WINDOW = '//////////'

const IDENTITY_HEADER_FILL = XLSX_HEADER_FILL
const ZEBRA_EVEN = 'FFF5F8FC'
const ZEBRA_ODD = 'FFFFFFFF'
const MONEY_NUMFMT = '#,##0.00'

interface IdentityColumn {
  header: string
  width: number
  value: (row: BenefitRow) => string
}

const titleCase = (value: string): string =>
  value.length > 0
    ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()
    : ''

const formatDate = (value: string | null): string =>
  value ? dayjs(value).format('MM/DD/YYYY') : ''

const IDENTITY_COLUMNS: IdentityColumn[] = [
  { header: 'Benefit ID', width: 38, value: (r) => r.benefitId },
  {
    header: 'Employee Public Key',
    width: 38,
    value: (r) => r.employeePublicKey,
  },
  { header: 'Company Name', width: 26, value: (r) => r.companyName ?? '' },
  { header: 'Employee Name', width: 22, value: (r) => r.employeeName ?? '' },
  {
    header: 'Relationship',
    width: 14,
    value: (r) => {
      const insured = primaryInsured(r)
      return insured ? titleCase(insured.relationship) : ''
    },
  },
  { header: 'Carrier', width: 22, value: (r) => r.carrierName ?? '' },
  {
    header: 'VCN Status',
    width: 14,
    value: (r) => (r.employmentStatus ? titleCase(r.employmentStatus) : ''),
  },
  {
    header: 'Monthly Pay Method',
    width: 18,
    value: (r) =>
      r.latestPaymentType ? formatPaymentLabel(r.latestPaymentType) : '',
  },
  {
    header: 'Effective Date',
    width: 14,
    value: (r) => formatDate(r.benefitEffectiveDate),
  },
  { header: 'End Date', width: 14, value: (r) => formatDate(r.benefitEndDate) },
]

interface MonthCell {
  /** Number when an expected resolves; the placeholder out-of-window; else ''. */
  prem: number | string
  /** Numeric for in-window months (paid, defaulting to 0); placeholder out-of-window. */
  draft: number | string
  trans: string
  date: string
}

const roundMoney = (value: number): number => Math.round(value * 100) / 100

/**
 * One month's four cells. In-window expected/paid resolution is kept identical
 * to the grid row summary in BenefitTableRow.tsx:
 *   paid     = monthlyTotal.totalPaid ?? 0
 *   expected = monthlyTotal.expected ?? (carrierExpected ?? surecoExpected)
 * so the exported Sum Prem / Sum Draft / DIFF reconcile against the on-screen
 * grid the operator exported from — including future in-window months the
 * backend may not have gap-filled yet.
 */
const buildMonthCell = (
  row: BenefitRow,
  year: number,
  monthIndex: number,
  rowDefaultExpected: number | null
): MonthCell => {
  const total = row.monthlyTotals[buildPeriodKey(year, monthIndex)]
  const { outOfRange } = monthCoverage(row, year, monthIndex)
  // An out-of-window month keeps the source sheet's placeholder only while it
  // is genuinely empty. Once an operator has deliberately allocated to it, the
  // export must carry the money: blanking a populated month would hide real
  // payments from the sheet finance reconciles against, and the Sum columns
  // below (which mirror BenefitTableRow's row summary) would not add up.
  if (outOfRange && (total?.payments?.length ?? 0) === 0) {
    return {
      prem: OUT_OF_WINDOW,
      draft: OUT_OF_WINDOW,
      trans: OUT_OF_WINDOW,
      date: OUT_OF_WINDOW,
    }
  }
  // Same rule as the row summary: no default-expected fallback out of window,
  // because nothing was owed for a month the benefit was not in force.
  const expected = outOfRange
    ? (total?.expected ?? null)
    : (total?.expected ?? rowDefaultExpected)
  const payments = total?.payments ?? []
  const trans = payments
    .map((payment) => payment.token ?? payment.confirmationCode)
    .filter((value): value is string => !!value)
    .join(' | ')
  const dates = payments
    .map((payment) => payment.transactionDate)
    .filter((value): value is string => !!value)
    .map((value) => dayjs(value))
  const latest =
    dates.length > 0 ? dates.reduce((a, b) => (a.isAfter(b) ? a : b)) : null
  return {
    prem: expected ?? '',
    draft: total?.totalPaid ?? 0,
    trans,
    date: latest ? latest.format('MM/DD/YYYY') : '',
  }
}

const monthColumnKeys = (monthIndex: number) => ({
  prem: `m${monthIndex}_prem`,
  draft: `m${monthIndex}_draft`,
  trans: `m${monthIndex}_trans`,
  date: `m${monthIndex}_date`,
})

const buildColumns = (
  visibleMonthIndices: number[]
): Partial<ExcelJS.Column>[] => {
  const columns: Partial<ExcelJS.Column>[] = IDENTITY_COLUMNS.map((col, i) => ({
    header: col.header,
    key: `id${i}`,
    width: col.width,
  }))
  visibleMonthIndices.forEach((mi) => {
    const keys = monthColumnKeys(mi)
    columns.push({ header: `${MONTHS[mi]} Prem`, key: keys.prem, width: 12 })
    columns.push({ header: `${MONTHS[mi]} Draft`, key: keys.draft, width: 12 })
    columns.push({ header: `${MONTHS[mi]} Trans`, key: keys.trans, width: 16 })
    columns.push({ header: `${MONTHS[mi]} Date`, key: keys.date, width: 13 })
  })
  columns.push({ header: 'Sum Prem', key: 'sumPrem', width: 12 })
  columns.push({ header: 'Sum Draft', key: 'sumDraft', width: 12 })
  columns.push({ header: 'DIFF', key: 'diff', width: 12 })
  columns.push({ header: 'Zendesk', key: 'zendesk', width: 34 })
  return columns
}

const buildRecord = (
  row: BenefitRow,
  year: number,
  visibleMonthIndices: number[]
): Record<string, string | number> => {
  const record: Record<string, string | number> = {}
  IDENTITY_COLUMNS.forEach((col, i) => {
    record[`id${i}`] = col.value(row)
  })
  const rowDefaultExpected = row.carrierExpected ?? row.surecoExpected
  let sumPrem = 0
  let sumDraft = 0
  let anyExpected = false
  visibleMonthIndices.forEach((mi) => {
    const cell = buildMonthCell(row, year, mi, rowDefaultExpected)
    const keys = monthColumnKeys(mi)
    record[keys.prem] = cell.prem
    record[keys.draft] = cell.draft
    record[keys.trans] = cell.trans
    record[keys.date] = cell.date
    if (typeof cell.prem === 'number') {
      sumPrem += cell.prem
      anyExpected = true
    }
    if (typeof cell.draft === 'number') sumDraft += cell.draft
  })
  // Mirror the grid: Sum Exp / DIFF are blank when no visible month carries an
  // expected; Sum Draft (paid) always shows.
  record.sumPrem = anyExpected ? roundMoney(sumPrem) : ''
  record.sumDraft = roundMoney(sumDraft)
  record.diff = anyExpected ? roundMoney(sumDraft - sumPrem) : ''
  record.zendesk = row.latestTicketUrl ?? ''
  return record
}

const styleHeaderRow = (headerRow: ExcelJS.Row): void => {
  headerRow.height = 22
  headerRow.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 }
    cell.fill = IDENTITY_HEADER_FILL
    cell.alignment = { vertical: 'middle', horizontal: 'left' }
    cell.border = { bottom: { style: 'thin', color: { argb: 'FFAAAAAA' } } }
  })
}

const applyMoneyFormat = (
  worksheet: ExcelJS.Worksheet,
  visibleMonthIndices: number[]
): void => {
  const moneyKeys = ['sumPrem', 'sumDraft', 'diff']
  visibleMonthIndices.forEach((mi) => {
    const keys = monthColumnKeys(mi)
    moneyKeys.push(keys.prem, keys.draft)
  })
  moneyKeys.forEach((key) => {
    worksheet.getColumn(key).numFmt = MONEY_NUMFMT
  })
}

const styleDataRows = (worksheet: ExcelJS.Worksheet): void => {
  for (let r = 2; r <= worksheet.rowCount; r++) {
    const dataRow = worksheet.getRow(r)
    const isEven = r % 2 === 0
    dataRow.eachCell({ includeEmpty: true }, (cell) => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: isEven ? ZEBRA_EVEN : ZEBRA_ODD },
      }
      cell.alignment = { vertical: 'middle' }
      cell.border = { bottom: { style: 'hair', color: { argb: 'FFEEEEEE' } } }
    })
  }
}

/** Number of frozen identity columns at the left of the sheet. */
export const IDENTITY_COLUMN_COUNT = IDENTITY_COLUMNS.length

/**
 * Build the styled workbook (pure — no download). Split from
 * {@link exportBenefitsXlsx} so the column layout and value mapping are unit
 * testable without touching the DOM.
 */
export const buildBenefitsWorkbook = ({
  rows,
  visibleMonthIndices,
  year,
}: {
  rows: BenefitRow[]
  visibleMonthIndices: number[]
  year: number
}): ExcelJS.Workbook => {
  const workbook = new ExcelJS.Workbook()
  const worksheet = workbook.addWorksheet(`Payments ${year}`)
  worksheet.columns = buildColumns(visibleMonthIndices)
  rows.forEach((row) =>
    worksheet.addRow(buildRecord(row, year, visibleMonthIndices))
  )
  styleHeaderRow(worksheet.getRow(1))
  applyMoneyFormat(worksheet, visibleMonthIndices)
  styleDataRows(worksheet)
  xlsxFreezePane(worksheet, IDENTITY_COLUMNS.length, 1)
  return workbook
}

/**
 * Build and download the styled XLSX. Sheet name and filename carry the year;
 * `downloadXlsx` appends a local timestamp so repeated exports never collide.
 */
export const exportBenefitsXlsx = async ({
  rows,
  visibleMonthIndices,
  year,
}: {
  rows: BenefitRow[]
  visibleMonthIndices: number[]
  year: number
}): Promise<void> => {
  const workbook = buildBenefitsWorkbook({ rows, visibleMonthIndices, year })
  await downloadXlsx(workbook, `reconciliation-payments-${year}`)
}
